import React from 'react';
import { FileText, Building2, MapPin, Layers } from 'lucide-react';
import C from '../../constants/colors';
import StatBadge from '../ui/StatBadge';
import StatusDot from '../ui/StatusDot';

export default function HeadcountView({ companies, sites, zones, employees }) {
  const onSite = employees.filter(e => e.currentSiteId).length;
  const inZone = employees.filter(e => e.currentZoneId).length;
  const offSite = employees.filter(e => !e.currentSiteId).length;
  const total = employees.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={20} color={C.blue} />Headcount Report
          </h2>
          <button onClick={() => window.print()} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FileText size={13} />Print Report
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <StatBadge label="Total Registered" value={total} color={C.blue} />
          <StatBadge label="On-site" value={onSite} color={C.green} />
          <StatBadge label="In Zone" value={inZone} color={C.blue} />
          <StatBadge label="Off-site" value={offSite} color={C.textMuted} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {companies.map(company => {
          const companySites = sites.filter(s => s.companyId === company.id);
          const companyEmps = employees.filter(e => e.companyId === company.id);
          if (companyEmps.length === 0) return null;
          return (
            <div key={company.id} style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', paddingBottom: '10px', borderBottom: `2px solid ${C.border}` }}>
                <Building2 size={16} color={C.blue} />
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>{company.name}</h3>
                <span style={{ fontSize: '12px', color: C.textMuted }}>{companyEmps.length} employees</span>
              </div>

              {companySites.map(site => {
                const siteZones = zones.filter(z => z.siteId === site.id);
                const siteEmps = employees.filter(e => e.homeSiteId === site.id);
                const onSiteCount = siteEmps.filter(e => e.currentSiteId === site.id).length;
                const offSiteCount = siteEmps.filter(e => !e.currentSiteId).length;

                return (
                  <div key={site.id} style={{ marginBottom: '20px', marginLeft: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <MapPin size={13} color={C.textSecondary} />
                      <span style={{ fontSize: '14px', fontWeight: 600, color: C.textPrimary }}>{site.name}</span>
                      <span style={{ fontSize: '11px', color: C.textMuted }}>{site.address}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '11px', color: C.green }}>{onSiteCount} on-site</span>
                      <span style={{ fontSize: '11px', color: C.textMuted }}>{offSiteCount} off-site</span>
                    </div>

                    {/* Unzoned employees on site */}
                    {(() => {
                      const unzoned = siteEmps.filter(e => e.currentSiteId === site.id && !e.currentZoneId);
                      if (!unzoned.length) return null;
                      return (
                        <div style={{ marginLeft: '20px', marginBottom: '10px' }}>
                          <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>On-site (no zone)</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {unzoned.map(emp => (
                              <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '5px 10px' }}>
                                <StatusDot status={emp.status} size={8} />
                                <span style={{ fontSize: '12px', color: C.textPrimary }}>{emp.name}</span>
                                <span style={{ fontSize: '10px', color: C.textMuted }}>{emp.employeeId}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Zone sections */}
                    {siteZones.map(zone => {
                      const zoneEmps = employees.filter(e => e.currentZoneId === zone.id);
                      if (!zoneEmps.length) return null;
                      return (
                        <div key={zone.id} style={{ marginLeft: '20px', marginBottom: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                            <Layers size={11} color={C.textMuted} />
                            <span style={{ fontSize: '11px', fontWeight: 600, color: C.textSecondary }}>{zone.name}</span>
                            <span style={{ fontSize: '10px', color: C.textMuted }}>Floor {zone.floor} · {zoneEmps.length}/{zone.capacity||'∞'}</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginLeft: '16px' }}>
                            {zoneEmps.map(emp => (
                              <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.surfaceElevated, border: `1px solid ${emp.status==='NEED_HELP'?C.red+'44':C.border}`, borderRadius: '8px', padding: '5px 10px' }}>
                                <StatusDot status={emp.status} size={8} />
                                <span style={{ fontSize: '12px', color: C.textPrimary }}>{emp.name}</span>
                                <span style={{ fontSize: '10px', color: C.textMuted }}>{emp.department}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Off-site */}
                    {(() => {
                      const absent = siteEmps.filter(e => !e.currentSiteId);
                      if (!absent.length) return null;
                      return (
                        <div style={{ marginLeft: '20px', marginBottom: '10px' }}>
                          <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Off-site</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {absent.map(emp => (
                              <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '5px 10px', opacity: 0.6 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.textMuted }} />
                                <span style={{ fontSize: '12px', color: C.textMuted }}>{emp.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
